import React, { useMemo } from 'react';
import { Mui } from '@osu/react-ui';
import StudentDashboard from '../../StudentDashboard';
import { useAdminContext } from '../context/AdminContext';
import withReadOnlyEnforcer from './ReadOnlyEnforcer';
import { AdminEmplIdOverrideContext } from '../../StudentDashboard/hooks/useUserEmplid';

/**
 * AdminEmplIdProvider - wraps components with emplid override for admin view
 * This allows StudentDashboard to fetch data for the selected student
 */
function AdminEmplIdProvider({ children, studentId, studentInfo }) {
  const contextValue = useMemo(() => ({
    emplid: studentId, // Use the selected student's ID
    studentInfo,
    isAdminOverride: true
  }), [studentId, studentInfo]);

  return (
    <AdminEmplIdOverrideContext.Provider value={contextValue}>
      {children}
    </AdminEmplIdOverrideContext.Provider>
  );
}

// Note: We use the existing Redux store instead of creating a new one
// to avoid issues with missing reducers that StudentDashboard might need

/**
 * AdminStudentDashboardWrapper Component
 * 
 * Wraps the StudentDashboard component with admin context and read-only enforcement.
 * This component:
 * - Overrides the emplid context so StudentDashboard fetches data for the selected student
 * - Applies read-only enforcement to prevent any actions/submissions
 * - Provides proper accessibility and visual feedback for admin mode
 * - Uses the same StudentDashboard component that students see, ensuring consistency
 * 
 * @param {Object} props - Component props
 * @param {string} props.studentId - ID of the student whose dashboard to display (emplid)
 * @param {Object} props.studentInfo - Student information object
 * @param {string} props.studentInfo.id - Student ID
 * @param {string} props.studentInfo.name - Student full name
 * @param {string} props.studentInfo.email - Student email address
 * @param {string[]} props.studentInfo.programs - Array of program names
 * @returns {JSX.Element} AdminStudentDashboardWrapper component
 */
function AdminStudentDashboardWrapper({ studentId, studentInfo }) {
  const { isAdminView } = useAdminContext();



  // Validate required props
  if (!studentId || !studentInfo) {
    return (
      <div role="alert" aria-live="assertive">
        <p>Error: Student information is required to display dashboard.</p>
      </div>
    );
  }

  // Validate admin context
  if (!isAdminView) {
    return (
      <div role="alert" aria-live="assertive">
        <p>Error: This component can only be used in admin view mode.</p>
      </div>
    );
  }



  return (
    <AdminEmplIdProvider studentId={studentId} studentInfo={studentInfo}>
      <div
        className="admin-student-dashboard-wrapper"
        data-testid="admin-student-dashboard-wrapper"
        role="main"
        aria-label={`Student dashboard for ${studentInfo.name} (Admin View)`}
      >


        {/* Screen reader announcement for admin context */}
        <div
          aria-live="polite"
          aria-atomic="true"
          className="sr-only"
          role="status"
          style={{
            position: 'absolute',
            left: '-10000px',
            width: '1px',
            height: '1px',
            overflow: 'hidden'
          }}
        >
          Now viewing {studentInfo.name}'s dashboard in admin mode using Student Dashboard. All actions are disabled.
        </div>

        {/* Dashboard - Student Dashboard with Error Boundary */}
        <Mui.Box>
          <React.Suspense fallback={
            <Mui.Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <Mui.CircularProgress />
            </Mui.Box>
          }>
            {React.createElement(withReadOnlyEnforcer(StudentDashboard))}
          </React.Suspense>
        </Mui.Box>
      </div>
    </AdminEmplIdProvider>
  );
}

export default AdminStudentDashboardWrapper;